import React from 'react';
/**
 * Utility functions to generate mock data for different time periods
 */
// Generate hourly data for today
const generateTodayData = (baseAmount, baseCount) => {
  return Array.from({
    length: 24
  }, (_, i) => {
    // Create a pattern that simulates typical business hours with peaks
    let hourFactor = 0.5; // Base factor for off-hours
    // Morning peak (9am-11am)
    if (i >= 9 && i <= 11) {
      hourFactor = 1.5;
    }
    // Lunch hour dip (12pm)
    else if (i === 12) {
      hourFactor = 0.8;
    }
    // Afternoon peak (1pm-5pm)
    else if (i >= 13 && i <= 17) {
      hourFactor = 1.3;
    }
    // Evening activity (6pm-8pm)
    else if (i >= 18 && i <= 20) {
      hourFactor = 0.9;
    }
    // Night (after 8pm)
    else if (i > 20) {
      hourFactor = 0.3;
    }
    // Early morning (before 9am)
    else {
      hourFactor = 0.2;
    }
    // Add some randomness
    hourFactor *= 0.9 + Math.random() * 0.2;
    const now = new Date();
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), i);
    return {
      date: date.toISOString(),
      hour: `${i}:00`,
      amount: Math.floor(baseAmount * hourFactor),
      count: Math.floor(baseCount * hourFactor)
    };
  });
};
// Generate payment volume data for different time periods
export const generatePaymentVolumeData = (timeRange: string) => {
  if (timeRange === 'today') {
    return generateTodayData(750000, 30);
  } else if (timeRange === '7d') {
    return [{
      date: '2023-06-01',
      amount: 13500000
    }, {
      date: '2023-06-02',
      amount: 15600000
    }, {
      date: '2023-06-03',
      amount: 14700000
    }, {
      date: '2023-06-04',
      amount: 14100000
    }, {
      date: '2023-06-05',
      amount: 15900000
    }, {
      date: '2023-06-06',
      amount: 16800000
    }, {
      date: '2023-06-07',
      amount: 18600000
    }];
  } else if (timeRange === '30d') {
    return Array.from({
      length: 30
    }, (_, i) => {
      const date = new Date(2023, 5, i + 1);
      return {
        date: date.toISOString().split('T')[0],
        amount: 12000000 + Math.floor(Math.random() * 9000000)
      };
    });
  } else {
    // 1y - Generate monthly data
    return Array.from({
      length: 12
    }, (_, i) => {
      const date = new Date(2023, i, 15);
      const seasonalFactor = i >= 9 || i <= 1 ? 1.2 : 1; // Higher volumes during holiday season
      return {
        date: date.toISOString().split('T')[0],
        amount: Math.floor((360000000 + Math.random() * 120000000) * seasonalFactor)
      };
    });
  }
};
// Generate previous period data for comparison
export const generatePreviousPeriodData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  if (timeRange === 'today') {
    // Yesterday's data (slightly different pattern)
    return generateTodayData(690000, 28).map(item => ({
      ...item,
      isPrevious: true
    }));
  } else if (timeRange === '7d') {
    // Previous 7 days
    return currentData.map(item => ({
      ...item,
      date: item.date,
      amount: Math.floor(item.amount * (0.85 + Math.random() * 0.2)),
      isPrevious: true
    }));
  } else if (timeRange === '30d') {
    // Previous 30 days
    return currentData.map(item => ({
      ...item,
      date: item.date,
      amount: Math.floor(item.amount * (0.85 + Math.random() * 0.2)),
      isPrevious: true
    }));
  } else {
    // Previous year
    return currentData.map(item => ({
      ...item,
      date: item.date,
      amount: Math.floor(item.amount * (0.75 + Math.random() * 0.2)),
      isPrevious: true
    }));
  }
};
// Generate transaction count data for different time periods
export const generateTransactionCountData = (timeRange: string) => {
  if (timeRange === 'today') {
    return generateTodayData(750000, 30).map(({
      date,
      hour,
      count
    }) => ({
      date,
      hour,
      count
    }));
  } else if (timeRange === '7d') {
    return [{
      date: '2023-06-01',
      count: 530
    }, {
      date: '2023-06-02',
      count: 565
    }, {
      date: '2023-06-03',
      count: 551
    }, {
      date: '2023-06-04',
      count: 500
    }, {
      date: '2023-06-05',
      count: 582
    }, {
      date: '2023-06-06',
      count: 583
    }, {
      date: '2023-06-07',
      count: 626
    }];
  } else if (timeRange === '30d') {
    return Array.from({
      length: 30
    }, (_, i) => {
      const date = new Date(2023, 5, i + 1);
      return {
        date: date.toISOString().split('T')[0],
        count: 450 + Math.floor(Math.random() * 250)
      };
    });
  } else {
    // 1y - Generate monthly data
    return Array.from({
      length: 12
    }, (_, i) => {
      const date = new Date(2023, i, 15);
      const seasonalFactor = i >= 9 || i <= 1 ? 1.2 : 1; // Higher volumes during holiday season
      return {
        date: date.toISOString().split('T')[0],
        count: Math.floor((15000 + Math.random() * 5000) * seasonalFactor)
      };
    });
  }
};
// Generate previous period transaction count data
export const generatePreviousTransactionCountData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  if (timeRange === 'today') {
    // Yesterday's data
    return generateTodayData(690000, 28).map(({
      date,
      hour,
      count
    }) => ({
      date,
      hour,
      count,
      isPrevious: true
    }));
  } else {
    // Previous period (slightly lower numbers)
    return currentData.map(item => ({
      ...item,
      date: item.date,
      count: Math.floor(item.count * (0.85 + Math.random() * 0.2)),
      isPrevious: true
    }));
  }
};
// Generate average transaction data for different time periods
export const generateAverageTransactionData = (timeRange: string) => {
  if (timeRange === 'today') {
    return generateTodayData(750000, 30).map(({
      date,
      hour,
      amount,
      count
    }) => ({
      date,
      hour,
      amount: Math.floor(amount / count)
    }));
  } else if (timeRange === '7d') {
    return [{
      date: '2023-06-01',
      amount: 25500
    }, {
      date: '2023-06-02',
      amount: 27600
    }, {
      date: '2023-06-03',
      amount: 26700
    }, {
      date: '2023-06-04',
      amount: 28200
    }, {
      date: '2023-06-05',
      amount: 27300
    }, {
      date: '2023-06-06',
      amount: 28800
    }, {
      date: '2023-06-07',
      amount: 29700
    }];
  } else if (timeRange === '30d') {
    return Array.from({
      length: 30
    }, (_, i) => {
      const date = new Date(2023, 5, i + 1);
      return {
        date: date.toISOString().split('T')[0],
        amount: 24000 + Math.floor(Math.random() * 7500)
      };
    });
  } else {
    // 1y - Generate monthly data
    return Array.from({
      length: 12
    }, (_, i) => {
      const date = new Date(2023, i, 15);
      return {
        date: date.toISOString().split('T')[0],
        amount: 25500 + Math.floor(Math.random() * 6000)
      };
    });
  }
};
// Generate refund data over time
export const generateRefundData = (timeRange: string) => {
  // Base the refund data on a percentage of the payment volume
  const paymentData = generatePaymentVolumeData(timeRange);
  return paymentData.map(item => {
    // Refunds are typically 2-5% of transaction volume
    const refundPercentage = 0.02 + Math.random() * 0.03;
    return {
      date: item.date,
      hour: item.hour,
      amount: Math.round(item.amount * refundPercentage),
      count: Math.round(item.amount * refundPercentage / 25500) // Assuming average refund is 25500 LKR
    };
  });
};
// Generate previous period refund data
export const generatePreviousRefundData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  // Previous period (slightly different numbers)
  return currentData.map(item => ({
    ...item,
    date: item.date,
    amount: Math.floor(item.amount * (0.9 + Math.random() * 0.2)),
    count: Math.floor(item.count * (0.9 + Math.random() * 0.2)),
    isPrevious: true
  }));
};
// Generate payment methods data
export const generatePaymentMethodsData = () => {
  return [{
    name: 'Card Payments',
    value: 42,
    color: '#6E9FD9'
  }, {
    name: 'Multi Currency Card Payments',
    value: 20,
    color: '#D16B5F'
  }, {
    name: 'LankaPay',
    value: 15,
    color: '#7FC29B'
  }, {
    name: 'Alipay',
    value: 10,
    color: '#D9984B'
  }, {
    name: 'WechatPay',
    value: 7,
    color: '#9C7CB6'
  }, {
    name: 'UnionPay',
    value: 4,
    color: '#5DAFBA'
  }, {
    name: 'Cryptocurrency Payments',
    value: 2,
    color: '#8884d8'
  }];
};
// Generate conversion data
export const generateConversionData = () => {
  return [{
    name: 'Initiated',
    value: 1000,
    color: '#6E9FD9'
  }, {
    name: 'Attempted',
    value: 850,
    color: '#D9984B'
  }, {
    name: 'Successful',
    value: 720,
    color: '#7FC29B'
  }];
};
// Generate attempts data
export const generateAttemptsData = () => {
  return [{
    attempts: '1',
    count: 450
  }, {
    attempts: '2',
    count: 200
  }, {
    attempts: '3',
    count: 50
  }, {
    attempts: '4+',
    count: 20
  }];
};
// Generate geographic payment data
export const generateGeoPaymentData = () => {
  return [{
    country: 'United States',
    countryCode: 'US',
    amount: 37350000,
    transactions: 1240,
    lat: 37.0902,
    lng: -95.7129
  }, {
    country: 'United Kingdom',
    countryCode: 'GB',
    amount: 23490000,
    transactions: 835,
    lat: 55.3781,
    lng: -3.436
  }, {
    country: 'Germany',
    countryCode: 'DE',
    amount: 17010000,
    transactions: 623,
    lat: 51.1657,
    lng: 10.4515
  }, {
    country: 'France',
    countryCode: 'FR',
    amount: 12960000,
    transactions: 476,
    lat: 46.2276,
    lng: 2.2137
  }, {
    country: 'Japan',
    countryCode: 'JP',
    amount: 11670000,
    transactions: 412,
    lat: 36.2048,
    lng: 138.2529
  }, {
    country: 'Australia',
    countryCode: 'AU',
    amount: 9750000,
    transactions: 345,
    lat: -25.2744,
    lng: 133.7751
  }, {
    country: 'Canada',
    countryCode: 'CA',
    amount: 8940000,
    transactions: 318,
    lat: 56.1304,
    lng: -106.3468
  }, {
    country: 'India',
    countryCode: 'IN',
    amount: 7380000,
    transactions: 287,
    lat: 20.5937,
    lng: 78.9629
  }, {
    country: 'Brazil',
    countryCode: 'BR',
    amount: 5940000,
    transactions: 213,
    lat: -14.235,
    lng: -51.9253
  }, {
    country: 'Singapore',
    countryCode: 'SG',
    amount: 5250000,
    transactions: 186,
    lat: 1.3521,
    lng: 103.8198
  }, {
    country: 'Spain',
    countryCode: 'ES',
    amount: 4590000,
    transactions: 168,
    lat: 40.4637,
    lng: -3.7492
  }, {
    country: 'Netherlands',
    countryCode: 'NL',
    amount: 4260000,
    transactions: 156,
    lat: 52.1326,
    lng: 5.2913
  }, {
    country: 'South Korea',
    countryCode: 'KR',
    amount: 3840000,
    transactions: 142,
    lat: 35.9078,
    lng: 127.7669
  }, {
    country: 'Italy',
    countryCode: 'IT',
    amount: 3450000,
    transactions: 127,
    lat: 41.8719,
    lng: 12.5674
  }, {
    country: 'Hong Kong',
    countryCode: 'HK',
    amount: 2940000,
    transactions: 108,
    lat: 22.3193,
    lng: 114.1694
  }];
};
// Generate mock company data
export function generateCompanies() {
  return [{
    id: 'comp-1',
    name: 'Acme Inc'
  }, {
    id: 'comp-2',
    name: 'Global Ventures'
  }, {
    id: 'comp-3',
    name: 'TechSolutions'
  }, {
    id: 'comp-4',
    name: 'Retail Express'
  }, {
    id: 'comp-5',
    name: 'Sunshine Markets'
  }, {
    id: 'comp-6',
    name: 'Mountain View Enterprises'
  }, {
    id: 'comp-7',
    name: 'Ocean Blue Trading'
  }, {
    id: 'comp-8',
    name: 'Stellar Systems'
  }];
}
// Generate pending refunds data
export const generatePendingRefundsData = () => {
  // Generate between 5-15 pending refunds
  const count = 5 + Math.floor(Math.random() * 11);
  const pendingRefunds = [];
  for (let i = 0; i < count; i++) {
    pendingRefunds.push({
      id: `ref-${i + 1}`,
      customerId: `cust-${100 + Math.floor(Math.random() * 900)}`,
      amount: 7500 + Math.floor(Math.random() * 142500),
      date: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      reason: ['Customer request', 'Product defect', 'Wrong item', 'Late delivery', 'Other'][Math.floor(Math.random() * 5)]
    });
  }
  return pendingRefunds;
};
// Generate refund reasons data
export const generateRefundReasonsData = () => {
  return [{
    reason: 'Customer request',
    count: 42,
    color: '#6E9FD9'
  }, {
    reason: 'Product defect',
    count: 28,
    color: '#D16B5F'
  }, {
    reason: 'Wrong item',
    count: 15,
    color: '#7FC29B'
  }, {
    reason: 'Late delivery',
    count: 10,
    color: '#D9984B'
  }, {
    reason: 'Other',
    count: 5,
    color: '#9C7CB6'
  }];
};
// Filter data by selected companies
export function filterDataByCompanies(data, selectedCompanyIds) {
  // If no companies selected or all companies selected, return all data
  if (selectedCompanyIds.length === 0) {
    return data;
  }
  // In a real app, this would filter the data based on company IDs
  // For this demo, we'll simulate filtering by reducing values proportionally
  const filterRatio = selectedCompanyIds.length / 8; // 8 is the total number of companies
  return data.map(item => {
    if (typeof item.amount === 'number') {
      return {
        ...item,
        amount: Math.round(item.amount * filterRatio)
      };
    }
    if (typeof item.count === 'number') {
      return {
        ...item,
        count: Math.round(item.count * filterRatio)
      };
    }
    if (typeof item.value === 'number') {
      return {
        ...item,
        value: Math.round(item.value * filterRatio)
      };
    }
    return item;
  });
}